export type ValidationErrors = {
  [key: string]: ValidationErrorValue
}

export type ValidationErrorValue = string | (ValidationErrors | undefined)[]

export class MappingValidationError extends Error {
  errors: ValidationErrors

  constructor(errors: ValidationErrors) {
    super('Mapping validation failed')
    this.name = 'MappingValidationError'
    this.errors = errors
  }
}

export function validateMapping<TInput, TOutput>(build: Builder<TInput, TOutput>): (input: TInput) => TOutput {
  return input => {
    const { result, errors } = runShape(build(input, makeT<TInput>(), tArray))

    if (errors) {
      throw new MappingValidationError(errors)
    }

    return result
  }
}

type Builder<TInput, TOutput> = (input: TInput, t: ValidationThrow<TInput>, tArray: BoundArrayValidator<TInput>) => OutputShape<TOutput>

type ValidationThrow<TInput> = (label: keyof TInput, message: string) => never

type BoundArrayValidator<TInput> = <TItem, TItemOutput>(label: keyof TInput, items: TItem[], build: ArrayItemBuilder<TItem, TItemOutput>) => TItemOutput[]

type ArrayValidator = <TItem, TItemOutput>(label: PropertyKey, items: TItem[], build: ArrayItemBuilder<TItem, TItemOutput>) => TItemOutput[]

type ArrayItemBuilder<TItem, TItemOutput> = (item: TItem, t: ValidationThrow<TItem>, tArray: ArrayValidator) => TItemOutput

type OutputShape<TOutput> = {
  [K in keyof TOutput]: () => TOutput[K]
}

const makeT =
  <TInput>(): ValidationThrow<TInput> =>
  (label, message) => {
    throw new FieldValidationError(label as string, message)
  }

const tArray: ArrayValidator = (label, items, build) => {
  const itemErrors: (ValidationErrors | undefined)[] = []
  const results: unknown[] = []
  let hasErrors = false

  for (const item of items) {
    try {
      results.push(build(item, makeT(), tArray))
      itemErrors.push(undefined)
    } catch (e) {
      if (e instanceof FieldValidationError) {
        itemErrors.push({ [e.label]: e.value })
        hasErrors = true
        continue
      }
      throw e
    }
  }

  if (hasErrors) {
    throw new FieldValidationError(label as string, itemErrors)
  }

  return results as never[]
}

function runShape<T>(shape: OutputShape<T>): { result: T; errors: ValidationErrors | null } {
  const errors: ValidationErrors = {}
  const result = {} as T

  for (const key of Object.keys(shape) as Array<keyof T>) {
    try {
      result[key] = shape[key]()
    } catch (e) {
      if (e instanceof FieldValidationError) {
        errors[e.label] = e.value
        continue
      }
      throw e
    }
  }

  return { result, errors: Object.keys(errors).length > 0 ? errors : null }
}

class FieldValidationError {
  constructor(
    public label: string,
    public value: ValidationErrorValue
  ) {}
}
